import type { StandChart } from '@vv/common';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { accentVariable, PAVILION_ACCENTS } from '../../utils/pavilion-accent';

/** Inline SVG rather than a charting library: a few slices do not justify the
 *  bundle cost, and the design needs no axes, tooltips or interaction. */
export function PieChart({ chart }: { chart: StandChart }): ReactElement {
  const { t } = useTranslation();
  const total = chart.slices.reduce((sum, slice) => sum + slice.value, 0);
  const radius = 50;
  const circumference = 2 * Math.PI * radius;

  // The API returns counts and labels but no colours, so slices are coloured
  // from the shared palette in order.
  const arcs = chart.slices.reduce<
    { label: string; value: number; colour: string; dash: number; offset: number }[]
  >((acc, slice, index) => {
    const fraction = total === 0 ? 0 : slice.value / total;
    const dash = fraction * circumference;
    const previous = acc[acc.length - 1];
    const accent = PAVILION_ACCENTS[index % PAVILION_ACCENTS.length] ?? 'trade';

    return [
      ...acc,
      {
        label: slice.label,
        value: slice.value,
        colour: accentVariable(accent),
        dash,
        offset: previous === undefined ? 0 : previous.offset + previous.dash,
      },
    ];
  }, []);

  const title = t(`charts.${chart.title}`, { defaultValue: chart.title });

  return (
    <figure className="card px-4 py-6">
      <figcaption className="mb-4 text-center font-semibold text-fog-100">{title}</figcaption>

      <svg viewBox="0 0 120 120" className="mx-auto size-44" role="img" aria-label={title}>
        <g transform="rotate(-90 60 60)">
          {arcs.map((arc) => (
            <circle
              key={arc.label}
              cx="60"
              cy="60"
              r={radius}
              fill="none"
              stroke={arc.colour}
              strokeWidth="20"
              strokeDasharray={`${String(arc.dash)} ${String(circumference - arc.dash)}`}
              strokeDashoffset={-arc.offset}
            />
          ))}
        </g>
      </svg>

      <ul className="mt-5 flex flex-wrap justify-center gap-x-5 gap-y-2">
        {arcs.map((arc) => (
          <li key={arc.label} className="flex items-center gap-2 text-sm text-fog-300">
            <span
              aria-hidden="true"
              className="size-3 rounded-[3px]"
              style={{ backgroundColor: arc.colour }}
            />
            {t(`charts.slices.${arc.label}`, { defaultValue: arc.label })}
          </li>
        ))}
      </ul>
    </figure>
  );
}
